/**
 * Marketing Automation Insights Section
 * Section 6: Top messages, content attribution, ICP resonance
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface MarketingAutomationInsightsProps {
  data?: {
    topPerformingMessages?: Array<{
      id: string;
      title: string;
      performance: {
        views?: number;
        conversions?: number;
        roi?: number;
      };
    }>;
    contentRevenueAttribution?: Array<{
      contentId: string;
      revenue: number;
    }>;
  };
}

export function MarketingAutomationInsights({ data }: MarketingAutomationInsightsProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value?: number) => {
    if (value === undefined) return 'N/A';
    return `${value.toFixed(2)}%`;
  };

  return (
    <div className="space-y-6">
      {data?.topPerformingMessages && data.topPerformingMessages.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Top Performing Messages</CardTitle>
            <CardDescription>Best performing marketing content</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.topPerformingMessages.map((message, index) => (
                <div key={index} className="p-4 border rounded-lg">
                  <h4 className="font-semibold mb-2">{message.title}</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Views</span>
                      <div className="font-semibold">{message.performance.views || 0}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Conversions</span>
                      <div className="font-semibold">{message.performance.conversions || 0}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">ROI</span>
                      <div className="font-semibold">{formatPercent(message.performance.roi)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Marketing Automation Insights</CardTitle>
            <CardDescription>No data available yet</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Marketing performance data will appear here as campaigns run.
            </p>
          </CardContent>
        </Card>
      )}

      {data?.contentRevenueAttribution && data.contentRevenueAttribution.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Content → Revenue Attribution</CardTitle>
            <CardDescription>Revenue attributed to specific content</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.contentRevenueAttribution.map((item, index) => (
                <div key={index} className="flex justify-between items-center p-2 border rounded">
                  <span className="text-sm">Content {item.contentId.slice(0, 8)}</span>
                  <span className="font-semibold">{formatCurrency(item.revenue)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
